import React, { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import Layout from '../components/Layout'
import { useSatsueiDataToroku } from '../hooks/useSatsueiDataToroku'
import { useFileDragAndDrop } from '../hooks/useFileDragAndDrop'
import { useApiError } from '../../shared/hooks/useApiError'
import { useAuth } from '../../shared/auth'
import MessageDialog from '../../shared/ui/MessageDialog'
import ShareDialog from '../../shared/ui/ShareDialog'
import { satsueiDataTorokuService } from '../services/satsueiDataTorokuService'
import { userKanriService } from '../services/userKanriService'
import SatsueiDataTorokuMediaArea from '../components/satsueiDataToroku/SatsueiDataTorokuMediaArea'
import SatsueiDataTorokuFormArea from '../components/satsueiDataToroku/SatsueiDataTorokuFormArea'
import SearchBar from '../components/satsueiDataToroku/SearchBar'
import CopyModal from '../components/satsueiDataToroku/CopyModal'
import ImageEditModal from '../components/satsueiDataToroku/ImageEditModal'
import CompositeImageModal from '../components/satsueiDataToroku/CompositeImageModal'
import ImageHandwriteEditModal from '../components/satsueiDataToroku/ImageHandwriteEditModal'
import CameraModal from '../components/satsueiDataToroku/CameraModal'
import PixClientModal from '../components/satsueiDataToroku/PixClientModal'
import VoiceRecorderModal from '../components/VoiceRecorderModal'
import OCRModal from '../components/OCRModal'
import { MAX_UPLOAD_FILE_SIZE_BYTES, UPLOAD_FILE_SIZE_ERROR_MESSAGE } from '../constants/upload'
import { useNavigate } from 'react-router-dom'

const SatsueiDataToroku = () => {
  const location = useLocation()
  const { user: currentUserId } = useAuth()
  const navigate = useNavigate()
  // Initialize useApiError hook
  const {
    showErrorDialog,
    errorMessage,
    messageType,
    handleApiError,
    handleApiResponse,
    handleValidationErrors,
    closeErrorDialog,
    showSuccessMessage
  } = useApiError()

  const {
    // State
    objectId,
    isLoading,
    objectData,
    satsueiMemoCategoryId,
    kyotenId,
    satsueiGuideId,
    createUserName,
    createDatetime,
    updateUserName,
    updateDatetime,
    satsueiMemoCategories,
    kyotenList,
    satsueiGuideList,
    imageFolderList,
    selectedImageFolderId,
    satsueiMemoForm,
    formData,
    satsueiMemoColumnDataList,
    imageList,
    movieList,
    otherFileList,
    selectedMediaType,
    isSelectionMode,
    selectedItems,
    objectIdAutoFlg,
    objectExists,

    // Actions
    setObjectId,
    setKyotenId,
    setSatsueiGuideId,
    setSelectedMediaType,
    setImageList,
    setOtherFileList,
    setIsSelectionMode,
    setSelectedItems,
    loadObject,
    handleCategoryChange,
    handleImageFolderChange,
    registerObject,
    deleteObject,
    copyObject,
    getAdjacentObject,
    getMaxObjectId,
    checkObjectExists,
    updateFormData,
    toggleSelectionMode,
    toggleItemSelection,
    selectAllItems,
    clearAllSelections,
    loadImageList,
    loadMovieList,
    loadOtherFileList,
    loadImageFolderList
  } = useSatsueiDataToroku({
    handleApiError,
    handleApiResponse,
    handleValidationErrors
  })

  // Handle URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const objectIdFromUrl = urlParams.get('objectId')

    if (objectIdFromUrl && objectIdFromUrl !== objectId) {
      setObjectId(objectIdFromUrl)
      loadObject(objectIdFromUrl)
    }
    navigate(location.pathname, { replace: true })
  }, [location.search, objectId, setObjectId, loadObject])

  // Load current user feature permissions for button disable state
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (currentUserId == null) return
      try {
        const response = await userKanriService.getPixUserList({ pixUserId: currentUserId })
        if (response?.pixUserList?.length > 0) {
          const me = response.pixUserList[0]
          setUserFeaturePermissions({
            pixClientUsage: me.pixClientUsage ?? 0,
            pixCameraUsage: me.pixCameraUsage ?? 0,
            voiceRecognitionUsage: me.voiceRecognitionUsage ?? 0,
            ocrUsage: me.ocrUsage ?? 0
          })
        }
      } catch (err) {
        console.error('Failed to load user feature permissions:', err)
      }
    }
    loadUserPermissions()
  }, [currentUserId])

  // Local state
  const [showCopyModal, setShowCopyModal] = useState(false)
  const [showImageEditModal, setShowImageEditModal] = useState(false)
  const [showCompositeModal, setShowCompositeModal] = useState(false)
  const [showAnnotationModal, setShowAnnotationModal] = useState(false)
  const [showPixClientModal, setShowPixClientModal] = useState(false)
  const [showCameraModal, setShowCameraModal] = useState(false)
  const [showShareDialog, setShowShareDialog] = useState(false)
  const [showVoiceRecorderModal, setShowVoiceRecorderModal] = useState(false)
  const [showOCRModal, setShowOCRModal] = useState(false)
  const [shareData, setShareData] = useState(null)
  const [selectedImageForEdit, setSelectedImageForEdit] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  // Current user feature permissions (1 or 0) - used to disable buttons when not allowed
  const [userFeaturePermissions, setUserFeaturePermissions] = useState({
    pixClientUsage: 0,
    pixCameraUsage: 0,
    voiceRecognitionUsage: 0,
    ocrUsage: 0
  })

  // Drag and drop for file upload
  const handleFileDrop = async (files) => {
    if (!objectId) {
      handleValidationErrors('IDを入力してください')
      return
    }

    if (selectedMediaType === 'image' && selectedImageFolderId === 99) {
      handleValidationErrors('「全フォルダ表示」を選択している時はアップロードが出来ません')
      return
    }

    const MAX_MOVIE_COUNT = 5
    if (selectedMediaType === 'movie' && (movieList.length + files.length > MAX_MOVIE_COUNT)) {
      handleValidationErrors(`登録できる動画は${MAX_MOVIE_COUNT}つまでです`)
      return
    }

    const oversized = files.find((f) => f.size > MAX_UPLOAD_FILE_SIZE_BYTES)
    if (oversized) {
      handleValidationErrors(UPLOAD_FILE_SIZE_ERROR_MESSAGE)
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    try {
      let uploadSuccess = true

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        setUploadProgress(((i + 1) / files.length) * 100)

        const formData = new FormData()
        formData.append('objectId', objectId)
        formData.append('inputFile', file)
        formData.append('fileName', file.name)
        formData.append('fileSize', file.size)

        if (selectedMediaType === 'image') {
          formData.append('imageFolderId', selectedImageFolderId)
        }

        let uploadEndpoint
        switch (selectedMediaType) {
          case 'image':
            uploadEndpoint = 'uploadImage'
            break
          case 'movie':
            uploadEndpoint = 'uploadMovie'
            break
          case 'other':
            uploadEndpoint = 'uploadOtherFile'
            break
          default:
            throw new Error('Invalid media type')
        }

        const response = await satsueiDataTorokuService.uploadFile(uploadEndpoint, formData)

        // Check for business logic errors in the response
        // Note: uploadFile service returns response.data directly, not the full response object
        if (response) {
          const hasError = handleApiResponse(response, 'ファイルのアップロードに失敗しました')
          if (hasError) {
            uploadSuccess = false
            break // Stop processing if there's an error
          }
        }
      }

      // Only reload media lists and show success message if all uploads were successful
      if (uploadSuccess) {
        // Reload media lists after upload
        if (objectId) {
          await Promise.all([
            loadImageList(objectId),
            loadMovieList(objectId),
            loadOtherFileList(objectId),
            ...(selectedMediaType === 'image' ? [loadImageFolderList(objectId)] : [])
          ])
        }

        showSuccessMessage('ファイルのアップロードが完了しました')
      }
    } catch (err) {
      handleApiError(err, 'ファイルのアップロードに失敗しました')
      console.error('Upload failed:', err)
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const { isDragOver, getDropZoneProps } = useFileDragAndDrop(handleFileDrop,
    selectedMediaType === 'image' ? ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff'] :
      selectedMediaType === 'movie' ? ['mp4', 'avi', 'mov', 'wmv', 'flv', 'm4v'] :
        ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'],
    handleValidationErrors
  )

  // Handle ScanSnap scanner
  const handleScanSnap = async () => {
    // Validation
    if (!objectId) {
      handleValidationErrors('IDを入力してください')
      return
    }

    if (selectedImageFolderId === 99) {
      handleValidationErrors('「全フォルダ表示」を選択している時はアップロードが出来ません')
      return
    }

    // Check if ScanSnap SDK is available
    if (typeof window === 'undefined' || !window.scansnap || !window.scansnap.websdk) {
      handleValidationErrors('ScanSnap SDKが読み込まれていません')
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    try {
      // Track scanned files for upload
      const scannedFiles = []
      let scanError = null

      // Initialize ScanSnap SDK
      const initResult = await new Promise((resolve) => {
        window.scansnap.websdk.Initialize().done((resultCode) => {
          resolve(resultCode)
        }).fail(() => {
          resolve(-1)
        })
      })

      if (initResult !== 0) {
        // Provide specific error messages based on error codes
        let errorMessage = `スキャナーの初期化に失敗しました (エラーコード: ${initResult})`
        
        if (initResult === -2) {
          errorMessage += '\n\n【対処方法】\n' +
            'ScanSnap Web SDK Serviceが起動していません。初回インストールが必要です。\n\n' +
            '1. 管理者権限でコマンドプロンプトを起動\n' +
            '   （Windowsキー → 「cmd」と入力 → 右クリック → 「管理者として実行」）\n\n' +
            '2. サービスをインストール（パスは環境に合わせて変更）\n' +
            '   sc create "ScanSnapWebSDKService" binPath= "（ScanSnap Homeインストールディレクトリ）\\WebSdk\\service\\ScanSnapWebSDKService.exe" start= auto\n\n' +
            '3. サービスを起動\n' +
            '   sc start ScanSnapWebSDKService\n\n' +
            '4. ブラウザーを再起動して再度お試しください'
        } else if (initResult === -3) {
          errorMessage += '\n\n【対処方法】\n' +
            'サポートされていないOSバージョンです。ScanSnap HomeのサポートOSを確認してください。'
        } else if (initResult === -5) {
          errorMessage += '\n\n【対処方法】\n' +
            '予期しないエラーが発生しました。ScanSnap Homeが正しくインストールされているか確認してください。'
        }
        throw new Error(errorMessage)
      }

      // Register event handlers
      window.scansnap.websdk.RegisterEvent('OnScanToFile', async (fileId) => {
        try {
          // Get blob data for the scanned file
          const blobData = await new Promise((resolve, reject) => {
            window.scansnap.websdk.GetBlobData(fileId).done((data) => {
              resolve(data)
            }).fail((error) => {
              reject(error)
            })
          })

          if (blobData && blobData instanceof ArrayBuffer) {
            // Generate filename with timestamp
            const date = new Date()
            const timestamp = date.getFullYear() +
              String(date.getMonth() + 1).padStart(2, '0') +
              String(date.getDate()).padStart(2, '0') + '_' +
              String(date.getHours()).padStart(2, '0') +
              String(date.getMinutes()).padStart(2, '0') +
              String(date.getSeconds()).padStart(2, '0') +
              String(date.getMilliseconds()).padStart(3, '0')
            const fileName = `scan_${timestamp}.jpg`

            // Convert ArrayBuffer to File
            const file = new File([blobData], fileName, {
              type: 'image/jpeg'
            })

            scannedFiles.push({ file, fileId })
          }
        } catch (err) {
          console.error('Error getting blob data:', err)
          scanError = err
        }
      })

      window.scansnap.websdk.RegisterEvent('OnScanFinish', async (files) => {
        try {
          // Wait for all OnScanToFile async handlers to complete
          // OnScanFinish receives array of file IDs, so we wait until
          // scannedFiles.length matches files.length
          const maxWaitTime = 10000 // 10 seconds timeout
          const checkInterval = 100 // Check every 100ms
          const startTime = Date.now()

          while (scannedFiles.length < files.length && (Date.now() - startTime) < maxWaitTime) {
            await new Promise(resolve => setTimeout(resolve, checkInterval))
          }

          // Check if we timed out
          if (scannedFiles.length < files.length) {
            throw new Error(`一部のファイルの処理がタイムアウトしました (${scannedFiles.length}/${files.length} 完了)`)
          }

          if (scanError) {
            throw scanError
          }

          if (scannedFiles.length === 0) {
            setIsUploading(false)
            setUploadProgress(0)
            handleValidationErrors('スキャンされたファイルがありません')
            return
          }

          const scannedOversized = scannedFiles.find(({ file }) => file.size > MAX_UPLOAD_FILE_SIZE_BYTES)
          if (scannedOversized) {
            setIsUploading(false)
            setUploadProgress(0)
            handleValidationErrors(UPLOAD_FILE_SIZE_ERROR_MESSAGE)
            return
          }

          // Upload all scanned files
          let uploadSuccess = true
          for (let i = 0; i < scannedFiles.length; i++) {
            const { file } = scannedFiles[i]
            setUploadProgress(((i + 1) / scannedFiles.length) * 100)

            const formData = new FormData()
            formData.append('objectId', objectId)
            formData.append('inputFile', file)
            formData.append('fileName', file.name)
            formData.append('fileSize', file.size)
            formData.append('imageFolderId', selectedImageFolderId)

            const response = await satsueiDataTorokuService.uploadImage(formData)

            if (response) {
              const hasError = handleApiResponse(response, 'ファイルのアップロードに失敗しました')
              if (hasError) {
                uploadSuccess = false
                break
              }
            }
          }

          // Reload media lists and show success message if all uploads were successful
          if (uploadSuccess) {
            if (objectId) {
              await Promise.all([
                loadImageList(objectId),
                loadMovieList(objectId),
                loadOtherFileList(objectId),
                loadImageFolderList(objectId)
              ])
            }
            showSuccessMessage(`${scannedFiles.length}件のファイルのアップロードが完了しました`)
          }
        } catch (err) {
          handleApiError(err, 'スキャンしたファイルのアップロードに失敗しました')
          console.error('Upload failed:', err)
        } finally {
          setIsUploading(false)
          setUploadProgress(0)
        }
      })

      // Configure scan settings
      window.scansnap.websdk.compression = 5 // JPEG compression
      window.scansnap.websdk.format = 2 // JPEG file
      window.scansnap.websdk.scanMode = 2 // Fine quality

      // Start scanning
      // Note: Scan().done() resolves AFTER all OnScanToFile and OnScanFinish events have been fired
      // So when resultCode === 0, the scan completed successfully and events were triggered
      const scanResult = await new Promise((resolve) => {
        window.scansnap.websdk.Scan().done((resultCode) => {
          resolve(resultCode)
        }).fail(() => {
          resolve(-1)
        })
      })

      if (scanResult !== 0) {
        setIsUploading(false)
        setUploadProgress(0)
        throw new Error(`スキャンに失敗しました (エラーコード: ${scanResult})`)
      }

      // At this point, scanResult === 0 means:
      // 1. The scan operation completed successfully
      // 2. All OnScanToFile events have been fired (but async handlers may still be processing)
      // 3. OnScanFinish event has been fired (but async handler may still be processing)
      // The OnScanFinish handler will handle the upload and cleanup
    } catch (err) {
      setIsUploading(false)
      setUploadProgress(0)
      handleApiError(err, 'スキャナーの操作に失敗しました')
      console.error('ScanSnap error:', err)
    }
  }

  // Handle search
  const handleSearch = async () => {
    if (objectIdAutoFlg === 1) {
      // Auto mode - check if exists first
      const exists = await checkObjectExists(objectId)
      if (exists) {
        await loadObject(objectId)
      } else {
        handleValidationErrors('登録されていないIDです')
      }
    } else {
      // Manual mode - load directly
      await loadObject(objectId)
    }
  }

  // Handle new object creation
  const handleNewObject = async () => {
    try {
      const maxId = await getMaxObjectId()
      const newId = (parseInt(maxId) + 1).toString()
      setObjectId(newId)

      // Load form for new object
      if (satsueiMemoCategoryId) {
        await handleCategoryChange(satsueiMemoCategoryId)
      }
    } catch (err) {
      handleApiError(err, '新規オブジェクトの作成に失敗しました')
    }
  }

  // Handle registration
  const handleRegister = async () => {
    try {
      const result = await registerObject()
      // Only show success if registerObject returned a result (no error occurred)
      if (result) {
        showSuccessMessage(objectExists ? '更新が完了しました' : '登録が完了しました')
      }
    } catch (err) {
      // Error is already set in the hook
    }
  }

  // Handle deletion
  const handleDelete = async () => {
    await MessageDialog.showConfirm('確認', `${objectId}を削除してよろしいですか？\n削除したIDは戻せなくなります。`, async () => {
      try {
        const result = await deleteObject()
        if (result) {
          showSuccessMessage('削除しました')
        }
      } catch (err) {
        // Error is already set in the hook
      }
    })
  }

  // Handle copy
  const handleCopy = async (destinationId) => {
    try {
      // First check if the destination object ID already exists
      const objectCountResult = await satsueiDataTorokuService.getObjectCount(destinationId)

      // Check for business logic errors in the response
      const hasError = handleApiResponse(objectCountResult, 'オブジェクトIDの確認に失敗しました')
      if (hasError) {
        return
      }

      // If objectCount is 1, the ID is already in use
      if (objectCountResult.objectCount === 1) {
        handleValidationErrors(`ID「${destinationId}」は既に使用されています。別のIDを指定してください。`)
        return
      }

      // Proceed with copy if ID is available
      const result = await copyObject(destinationId)
      setShowCopyModal(false)
      // Only show success if copyObject returned a result (no error occurred)
      if (result) {
        showSuccessMessage(`コピーが完了しました。\n「${result.copyDestinationObjectId}」を表示します。`)

        // Navigate to copied object
        setObjectId(result.copyDestinationObjectId)
        await loadObject(result.copyDestinationObjectId)
      }
    } catch (err) {
      // Error is already set in the hook
    }
  }

  // Handle image edit
  const handleImageEdit = (image) => {
    setSelectedImageForEdit(image)
    setShowImageEditModal(true)
  }


  const handleImageReorder = (newImageList) => {
    // Update the order indices based on new positions (live reordering)
    const updatedImageList = newImageList.map((image, index) => ({
      ...image,
      orderIndex: index
    }))

    // Update local state immediately for live reordering
    setImageList(updatedImageList)

    // Note: API call will be made when user presses register button
    // This provides instant visual feedback without API delays
  }

  const handleOtherFileReorder = (newOtherFileList) => {
    // Update the order indices based on new positions (live reordering)
    const updatedOtherFileList = newOtherFileList.map((file, index) => ({
      ...file,
      orderIndex: index
    }))

    // Update local state immediately for live reordering
    setOtherFileList(updatedOtherFileList)

    // Note: API call will be made when user presses register button
    // This provides instant visual feedback without API delays
  }

  // Handle media type change
  const handleMediaTypeChange = (type) => {
    setSelectedMediaType(type)
    setIsSelectionMode(false)
    setSelectedItems([])
  }

  // Handle selection actions
  const handleSelectionAction = async (action) => {
    if (selectedItems.length === 0) {
      handleValidationErrors('削除するアイテムを選択してください')
      return
    }

    try {
      switch (action) {
        case 'delete':
          await MessageDialog.showConfirm('確認', `選択された${selectedItems.length}個のアイテムを削除しますか？`, async () => {
            let deleteSuccess = true

            for (const itemId of selectedItems) {
              try {
                let result
                switch (selectedMediaType) {
                  case 'image':
                    result = await satsueiDataTorokuService.removeImage(objectId, itemId)
                    break
                  case 'movie':
                    result = await satsueiDataTorokuService.removeMovie(objectId, itemId)
                    break
                  case 'other':
                    result = await satsueiDataTorokuService.removeOtherFile(objectId, itemId)
                    break
                }

                if (result) {
                  const hasError = handleApiResponse(result, 'アイテムの削除に失敗しました')
                  if (hasError) {
                    deleteSuccess = false
                    break
                  }
                }
              } catch (err) {
                handleApiError(err, 'アイテムの削除に失敗しました')
                deleteSuccess = false
                break
              }
            }

            if (deleteSuccess) {
              if (objectId) {
                await Promise.all([
                  loadImageList(objectId),
                  loadMovieList(objectId),
                  loadOtherFileList(objectId)
                ])
              }

              setIsSelectionMode(false)
              setSelectedItems([])
              showSuccessMessage('削除が完了しました')
            }
          })
          break
        default:
          break
      }
    } catch (err) {
      handleApiError(err, '削除に失敗しました')
    }
  }

  // Handle download
  const handleDownload = async () => {
    try {
      const date = new Date()
      const timestamp = date.getFullYear() + "-" +
        String(date.getMonth() + 1).padStart(2, '0') + "-" +
        String(date.getDate()).padStart(2, '0') + "-" +
        String(date.getHours()).padStart(2, '0') +
        String(date.getMinutes()).padStart(2, '0') +
        String(date.getSeconds()).padStart(2, '0')

      const fileName = `撮影データ${timestamp}`

      const data = {
        satsueiMemoCategoryId,
        objectId
      }

      const response = await satsueiDataTorokuService.getObjectListDownload(data)

      // Check for business logic errors in the response
      const hasError = handleApiResponse(response, 'ダウンロードに失敗しました')
      if (hasError) {
        return
      }

      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${fileName}.zip`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      showSuccessMessage('ダウンロードが完了しました')
    } catch (err) {
      handleApiError(err, 'ダウンロードに失敗しました')
    }
  }

  // Handle share
  const handleShare = async () => {
    try {
      const data = {
        objectId,
        shareable: "true"
      }

      const result = await satsueiDataTorokuService.addObjectShare(data)

      // Store share data and show dialog
      setShareData(result)
      setShowShareDialog(true)
    } catch (err) {
      handleApiError(err, 'シェア機能に失敗しました')
    }
  }

  // Handle copy to clipboard
  const handleCopyToClipboard = async () => {
    try {
      const shareText = `PC版 : ${shareData.sharePagePcUrl} \n` +
        `SP版 : ${shareData.sharePageUrl}`

      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareText)
        showSuccessMessage('シェア用URLをクリップボードにコピーしました')
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = shareText
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        showSuccessMessage('シェア用URLをクリップボードにコピーしました')
      }

      setShowShareDialog(false)
    } catch (err) {
      handleApiError(err, 'コピーに失敗しました')
    }
  }

  // Create mailto link using standard encoding (most globally acceptable)
  const createMailtoLink = (subject, body) => {
    // Use standard encodeURIComponent for both subject and body
    // const encodedSubject = encodeURIComponent(subject)
    // const encodedBody = encodeURIComponent(body)
    // return `mailto:?subject=${encodedSubject}&body=${encodedBody}`
    return `mailto:?subject=${subject}&body=${body}`
  }


  // Handle voice recognition
  const handleVoiceRecognition = useCallback(() => {
    if (!satsueiMemoCategoryId) {
      handleValidationErrors('カテゴリーを選択してください')
      return
    }
    setShowVoiceRecorderModal(true)
  }, [satsueiMemoCategoryId, handleValidationErrors])

  // Handle applying voice recognition data to form
  const handleApplyVoiceData = useCallback((extractedData) => {
    if (!extractedData || Object.keys(extractedData).length === 0) {
      return
    }

    // Choice field types that use option keys (radio, checkbox, multi-select, dropdown, n-stage)
    const choiceTypes = [5, 6, 7, 8, 11, 12, 13, 14]
    // Multi-select field types (checkbox, multi-select dropdown)
    const multiSelectTypes = [6, 7]
    // Date field types
    const dateTypes = [9, 10]

    // Helper function to parse Japanese date strings to backend format (YYYY/MM/DD or YYYY/MM/DD HH:mm)
    const parseDateValue = (value, isDateTime = false) => {
      if (!value) return null
      
      const currentYear = new Date().getFullYear()
      let year, month, day, hour, minute
      
      // Try various date patterns
      // Pattern 1: "2025年1月22日" or "2025年01月22日"
      let match = value.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/)
      if (match) {
        year = match[1]
        month = match[2].padStart(2, '0')
        day = match[3].padStart(2, '0')
      }
      
      // Pattern 2: "1月22日" (use current year)
      if (!year) {
        match = value.match(/(\d{1,2})月(\d{1,2})日/)
        if (match) {
          year = currentYear.toString()
          month = match[1].padStart(2, '0')
          day = match[2].padStart(2, '0')
        }
      }
      
      // Pattern 3: "2025/01/22" or "2025-01-22"
      if (!year) {
        match = value.match(/(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/)
        if (match) {
          year = match[1]
          month = match[2].padStart(2, '0')
          day = match[3].padStart(2, '0')
        }
      }
      
      // Pattern 4: "01/22" or "1/22" (use current year)
      if (!year) {
        match = value.match(/(\d{1,2})[\/\-](\d{1,2})/)
        if (match) {
          year = currentYear.toString()
          month = match[1].padStart(2, '0')
          day = match[2].padStart(2, '0')
        }
      }
      
      if (!year || !month || !day) return null
      
      // For datetime fields, try to extract time
      if (isDateTime) {
        // Pattern: "10時30分" or "10:30" or "10時"
        const timeMatch = value.match(/(\d{1,2})時(\d{1,2})分/) || 
                          value.match(/(\d{1,2}):(\d{1,2})/) ||
                          value.match(/(\d{1,2})時/)
        if (timeMatch) {
          hour = timeMatch[1].padStart(2, '0')
          minute = (timeMatch[2] || '00').padStart(2, '0')
          return `${year}/${month}/${day} ${hour}:${minute}`
        }
        return `${year}/${month}/${day} 00:00`
      }
      
      return `${year}/${month}/${day}`
    }

    // Helper function to find matching option (exact or partial match)
    const findMatchingOption = (options, searchValue) => {
      const searchLower = searchValue?.toLowerCase()?.trim()
      if (!searchLower) return null

      // First try exact match
      let match = options.find(
        opt => opt.satsueiMemoColumnValueName?.toLowerCase() === searchLower || 
               opt.key?.toLowerCase() === searchLower
      )
      
      // If no exact match, try partial match (e.g., "山売" matches "山売り")
      if (!match) {
        match = options.find(opt => {
          const optNameLower = opt.satsueiMemoColumnValueName?.toLowerCase()
          const optKeyLower = opt.key?.toLowerCase()
          return (optNameLower && (optNameLower.startsWith(searchLower) || searchLower.startsWith(optNameLower))) ||
                 (optKeyLower && (optKeyLower.startsWith(searchLower) || searchLower.startsWith(optKeyLower)))
        })
      }
      
      return match
    }

    // Convert display values to option keys for choice fields
    Object.entries(extractedData).forEach(([key, value]) => {
      // Find the column definition
      const column = satsueiMemoColumnDataList?.find(col => col.key === key)
      
      if (column && column.satsueiMemoColumnValueList?.length > 0) {
        // This is a choice field - find matching option by display name
        // Note: columnType might be string or number, so convert to number for comparison
        const columnTypeNum = parseInt(column.columnType, 10)
        if (choiceTypes.includes(columnTypeNum)) {
          
          // For multi-select fields (checkbox, multi-select dropdown), handle multiple values
          if (multiSelectTypes.includes(columnTypeNum)) {
            // Split by common delimiters: comma, Japanese comma, space, "と", "や"
            const valueParts = value?.split(/[,、\s]+|と|や/).filter(v => v.trim()) || []
            const matchedKeys = []
            
            for (const part of valueParts) {
              const matchingOption = findMatchingOption(column.satsueiMemoColumnValueList, part)
              if (matchingOption && !matchedKeys.includes(matchingOption.key)) {
                matchedKeys.push(matchingOption.key)
              }
            }
            
            if (matchedKeys.length > 0) {
              // Join multiple keys with comma for multi-select fields
              updateFormData(key, matchedKeys.join(','))
              return
            }
          } else {
            // Single-select fields (radio, dropdown, n-stage)
            const matchingOption = findMatchingOption(column.satsueiMemoColumnValueList, value)
            if (matchingOption) {
              updateFormData(key, matchingOption.key)
              return
            }
          }
        }
      }
      
      // Handle date fields (columnType 9 = date, 10 = datetime)
      if (column) {
        const columnTypeNum = parseInt(column.columnType, 10)
        if (dateTypes.includes(columnTypeNum)) {
          const isDateTime = columnTypeNum === 10
          const parsedDate = parseDateValue(value, isDateTime)
          if (parsedDate) {
            updateFormData(key, parsedDate)
            return
          }
        }
      }
      
      // For non-choice fields or no match found, use value directly
      updateFormData(key, value)
    })

    //showSuccessMessage(`${Object.keys(extractedData).length}件のデータをフォームに適用しました`)
  }, [updateFormData, satsueiMemoColumnDataList])

  // Handle OCR
  const handleOCR = useCallback(() => {
    if (!satsueiMemoCategoryId) {
      handleValidationErrors('カテゴリーを選択してください')
      return
    }
    setShowOCRModal(true)
  }, [satsueiMemoCategoryId, handleValidationErrors])

  // Handle uploading image from OCR modal (upload then return new image record for OCR)
  const handleOCRImageUpload = useCallback(async (file) => {
    if (!objectId) {
      throw new Error('IDを入力してください')
    }
    if (selectedImageFolderId === 99) {
      throw new Error('「全フォルダ表示」を選択している時はアップロードが出来ません')
    }
    if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
      throw new Error(UPLOAD_FILE_SIZE_ERROR_MESSAGE)
    }
    try {
      const formData = new FormData()
      formData.append('objectId', objectId)
      formData.append('inputFile', file)
      formData.append('fileName', file.name)
      formData.append('fileSize', file.size)
      formData.append('imageFolderId', selectedImageFolderId)
      const response = await satsueiDataTorokuService.uploadImage(formData)
      if (response && handleApiResponse(response, 'OCR用画像のアップロードに失敗しました')) {
        throw new Error('アップロードに失敗しました')
      }
      await Promise.all([
        loadImageList(objectId, selectedImageFolderId),
        loadImageFolderList(objectId)
      ])
      const listResult = await satsueiDataTorokuService.getImageList(objectId, selectedImageFolderId)
      const images = listResult?.objectDatas?.[0]?.objectImages || []
      if (images.length === 0) throw new Error('アップロード後の画像を取得できませんでした')
      const newest = images.reduce((a, b) => (a.objectImageId > b.objectImageId ? a : b))
      return newest
    } catch (err) {
      handleApiError(err, 'OCR用画像のアップロードに失敗しました')
      throw err
    }
  }, [objectId, selectedImageFolderId, loadImageList, loadImageFolderList, handleApiResponse, handleApiError])

  // Handle email share
  const handleEmailShare = () => {
    try {
      let subject, body

      if (shareData && shareData.sharePageTitle && shareData.sharePageBody) {
        subject = shareData.sharePageTitle
        body = shareData.sharePageBody
      } else {
        // Fallback if sharePageTitle/sharePageBody are not available
        subject = '【pix-label】画像がシェアされました。'
        body = `下記のURLから画像を閲覧できます。\n` +
          `PC版\n${window.location.origin}/pix/share?shareCode=${shareData.shareCode}\n` +
          `SP版\n${window.location.origin}/pix/appShare?shareCode=${shareData.shareCode}`
      }

      // Create mailto link using standard encoding
      const mailtoLink = createMailtoLink(subject, body)

      // Open email client
      window.location.href = mailtoLink

      setShowShareDialog(false)
    } catch (err) {
      handleApiError(err, 'メール送信に失敗しました')
    }
  }

  return (
    <Layout pageTitle="撮影データ登録">
      <div className="min-h-screen bg-gray-50 w-full">
        {/* Loading overlay */}
        {(isLoading || isUploading) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="loading-overlay">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span data-testid="loading-text">{isUploading ? 'アップロード中...' : '読み込み中...'}</span>
              </div>
              {isUploading && (
                <div className="mt-2 w-64 bg-gray-200 rounded-full h-2" data-testid="upload-progress-bar">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                    data-testid="upload-progress-fill"
                  ></div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Message Dialog */}
        <MessageDialog
          isOpen={showErrorDialog}
          onClose={closeErrorDialog}
          title={
            messageType === 'error' ? 'エラー' :
              messageType === 'success' ? '成功' :
                messageType === 'warning' ? '警告' :
                  '情報'
          }
          message={errorMessage}
          type={messageType}
          buttonText="閉じる"
          data-testid="message-dialog"
        />

        <div className="grid grid-cols-1 lg:grid-cols-10 gap-4 lg:gap-6" data-testid="main-content-grid">
          {/* Search Bar - Always visible */}
          <div className="lg:col-span-10" data-testid="search-bar-container">
            <SearchBar
              objectId={objectId}
              setObjectId={setObjectId}
              onSearch={handleSearch}
              onNew={handleNewObject}
              onCopy={() => setShowCopyModal(true)}
              onDelete={handleDelete}
              onRegister={handleRegister}
              onPrev={() => getAdjacentObject('prev')}
              onNext={() => getAdjacentObject('next')}
              objectIdAutoFlg={objectIdAutoFlg}
              hasData={!!objectData}
              objectExists={objectExists}
            />
          </div>

          {/* Left Panel - Search Bar and Media Area (70%) - Only show when objectData exists */}
          {objectData && (
            <>
              <div className="lg:col-span-7" data-testid="media-area-container">
                <SatsueiDataTorokuMediaArea
                  selectedMediaType={selectedMediaType}
                  onMediaTypeChange={handleMediaTypeChange}
                  imageList={imageList}
                  movieList={movieList}
                  otherFileList={otherFileList}
                  imageFolderList={imageFolderList}
                  selectedImageFolderId={selectedImageFolderId}
                  onImageFolderChange={handleImageFolderChange}
                  isSelectionMode={isSelectionMode}
                  selectedItems={selectedItems}
                  onToggleSelectionMode={toggleSelectionMode}
                  onToggleItemSelection={toggleItemSelection}
                  onSelectAll={selectAllItems}
                  onClearAll={clearAllSelections}
                  onSelectionAction={handleSelectionAction}
                  onImageEdit={handleImageEdit}
                  onDownload={handleDownload}
                  onShare={handleShare}
                  onComposite={() => setShowCompositeModal(true)}
                  onAnnotation={() => setShowAnnotationModal(true)}
                  onPixClient={() => setShowPixClientModal(true)}
                  onScanSnap={handleScanSnap}
                  onCamera={() => setShowCameraModal(true)}
                  onVoiceRecognition={handleVoiceRecognition}
                  onOCR={handleOCR}
                  cameraDisabled={userFeaturePermissions.pixCameraUsage !== 1}
                  pixClientDisabled={userFeaturePermissions.pixClientUsage !== 1}
                  voiceRecognitionDisabled={userFeaturePermissions.voiceRecognitionUsage !== 1}
                  ocrDisabled={userFeaturePermissions.ocrUsage !== 1}
                  userPixCameraUsage={userFeaturePermissions.pixCameraUsage}
                  userPixClientUsage={userFeaturePermissions.pixClientUsage}
                  userVoiceRecognitionUsage={userFeaturePermissions.voiceRecognitionUsage}
                  userOcrUsage={userFeaturePermissions.ocrUsage}
                  getDropZoneProps={getDropZoneProps}
                  isDragOver={isDragOver}
                  objectId={objectId}
                  onImageReorder={handleImageReorder}
                  onOtherFileReorder={handleOtherFileReorder}
                />
              </div>

              {/* Right Panel - Form Area (30%) */}
              <div className="lg:col-span-3" data-testid="form-area-container">
                <SatsueiDataTorokuFormArea
                  satsueiMemoCategories={satsueiMemoCategories}
                  satsueiMemoCategoryId={satsueiMemoCategoryId}
                  onCategoryChange={handleCategoryChange}
                  kyotenList={kyotenList}
                  kyotenId={kyotenId}
                  setKyotenId={setKyotenId}
                  satsueiGuideList={satsueiGuideList}
                  satsueiGuideId={satsueiGuideId}
                  setSatsueiGuideId={setSatsueiGuideId}
                  satsueiMemoForm={satsueiMemoForm}
                  formData={formData}
                  updateFormData={updateFormData}
                  createUserName={createUserName}
                  createDatetime={createDatetime}
                  updateUserName={updateUserName}
                  updateDatetime={updateDatetime}
                  hasData={!!objectData}
                  objectData={objectData}
                  satsueiMemoColumnDataList={satsueiMemoColumnDataList}
                />
              </div>
            </>
          )}
        </div>

        {/* Modals */}
        <div data-testid="modals-container">
          <CopyModal
            isOpen={showCopyModal}
            onClose={() => setShowCopyModal(false)}
            onCopy={handleCopy}
            objectId={objectId}
            data-testid="copy-modal"
          />

          <ImageEditModal
            isOpen={showImageEditModal}
            onClose={() => setShowImageEditModal(false)}
            image={selectedImageForEdit}
            objectId={objectId}
            onImageUpdate={() => {
              if (objectId) {
                loadImageList(objectId)
              }
            }}
            data-testid="image-edit-modal"
          />

          <CompositeImageModal
            isOpen={showCompositeModal}
            onClose={() => setShowCompositeModal(false)}
            imageList={imageList}
            objectId={objectId}
            onCompositeComplete={() => {
              if (objectId) {
                loadImageList(objectId)
              }
            }}
            data-testid="composite-image-modal"
          />

          <ImageHandwriteEditModal
            isOpen={showAnnotationModal}
            onClose={() => setShowAnnotationModal(false)}
            imageList={imageList}
            objectId={objectId}
            onAnnotationComplete={() => {
              if (objectId) {
                loadImageList(objectId)
              }
            }}
            data-testid="image-annotation-modal"
          />

          <PixClientModal
            isOpen={showPixClientModal}
            onClose={() => setShowPixClientModal(false)}
            data-testid="pixclient-modal"
          />

          <CameraModal
            isOpen={showCameraModal}
            onClose={() => setShowCameraModal(false)}
            objectId={objectId}
            imageFolderId={selectedImageFolderId}
            onSave={() => {
              if (objectId) {
                loadImageList(objectId)
              }
            }}
            data-testid="camera-modal"
          />

          <ShareDialog
            isOpen={showShareDialog}
            onClose={() => setShowShareDialog(false)}
            onCopy={handleCopyToClipboard}
            onEmail={handleEmailShare}
            shareData={shareData}
            data-testid="share-dialog"
          />

          <VoiceRecorderModal
            isOpen={showVoiceRecorderModal}
            onClose={() => setShowVoiceRecorderModal(false)}
            satsueiMemoCategoryId={parseInt(satsueiMemoCategoryId)}
            onApplyData={handleApplyVoiceData}
            data-testid="voice-recorder-modal"
          />

          <OCRModal
            isOpen={showOCRModal}
            onClose={() => setShowOCRModal(false)}
            satsueiMemoCategoryId={parseInt(satsueiMemoCategoryId)}
            onApplyData={handleApplyVoiceData}
            objectId={objectId}
            imageList={imageList}
            onUploadImage={handleOCRImageUpload}
            onApiError={handleApiError}
            getImagePreview={(objId, image) => satsueiDataTorokuService.getImage(objId, image.objectImageId, image.objectImageEditId ?? image.maxObjectImageEditId ?? null, image.filenameExtension, image.isUploaded)}
            data-testid="ocr-modal"
          />
        </div>

      </div>
    </Layout>
  )
}

export default SatsueiDataToroku
